
import { Subscription } from "@/types/subscription";
import { supabase } from "@/integrations/supabase/client";

export async function getSubscriptionLimits(subscription: Partial<Subscription> | null): Promise<{
  companiesLimit: number;
  emailsLimit: number;
  personResolutionsLimit: number;
}> {
  if (!subscription) {
    console.log("[getSubscriptionLimits] No subscription provided, returning zero limits");
    return { companiesLimit: 0, emailsLimit: 0, personResolutionsLimit: 0 };
  }

  try {
    console.log(`[${new Date().toISOString()}] [getSubscriptionLimits] Getting limits for plan: ${subscription.plan}`);
    console.log(`[${new Date().toISOString()}] [getSubscriptionLimits] Is custom plan: ${subscription.plan === 'custom'}`);
    console.log(`[${new Date().toISOString()}] [getSubscriptionLimits] Metadata:`, subscription.metadata);
    
    // For custom plans, use metadata values directly if available
    if (subscription.plan === 'custom') {
      console.log(`[${new Date().toISOString()}] [getSubscriptionLimits] Processing custom plan limits from metadata`);
      
      const customCompanyResolutions = subscription.metadata?.custom_resolutions;
      const customPersonResolutions = subscription.metadata?.custom_person_resolutions;
      
      console.log(`[${new Date().toISOString()}] [getSubscriptionLimits] Custom resolutions from metadata:`, {
        customCompanyResolutions,
        customPersonResolutions
      });
      
      if (customCompanyResolutions !== undefined && customPersonResolutions !== undefined) {
        console.log(`[${new Date().toISOString()}] [getSubscriptionLimits] Using custom limits from metadata:`, {
          companiesLimit: Number(customCompanyResolutions),
          emailsLimit: Number(customCompanyResolutions) * 2,
          personResolutionsLimit: Number(customPersonResolutions)
        });
        
        return {
          companiesLimit: Number(customCompanyResolutions),
          emailsLimit: Number(customCompanyResolutions) * 2,
          personResolutionsLimit: Number(customPersonResolutions)
        };
      }
      
      console.log(`[${new Date().toISOString()}] [getSubscriptionLimits] Custom plan but missing metadata values, will try fallback`);
    }
    
    // For trial plans or if missing custom plan values, check standard plan configuration
    if (subscription.plan === 'trial') {
      console.log(`[${new Date().toISOString()}] [getSubscriptionLimits] Trial plan, returning zero limits`);
      return { companiesLimit: 0, emailsLimit: 0, personResolutionsLimit: 0 };
    }
    
    console.log(`[${new Date().toISOString()}] [getSubscriptionLimits] Fetching plan limits from database for plan: ${subscription.plan}`);
    const { data: planConfig, error } = await supabase
      .rpc('get_plan_limits', { plan_name: subscription.plan });

    if (error) {
      console.error('[getSubscriptionLimits] Error getting plan limits:', error);
      return { companiesLimit: 0, emailsLimit: 0, personResolutionsLimit: 0 };
    }
    
    console.log(`[${new Date().toISOString()}] [getSubscriptionLimits] Plan config retrieved:`, planConfig);

    // Check if planConfig is an array and handle it appropriately
    const configData = Array.isArray(planConfig) ? planConfig[0] : planConfig;
    
    if (!configData) {
      console.error('[getSubscriptionLimits] No plan configuration data available');
      return { companiesLimit: 0, emailsLimit: 0, personResolutionsLimit: 0 };
    }
    
    // Log the actual values we're extracting
    console.log(`[${new Date().toISOString()}] [getSubscriptionLimits] Extracting standard limits:`, {
      companiesLimit: configData.company_resolutions_limit,
      personResolutionsLimit: configData.person_resolutions_limit
    });

    // For custom plans with missing metadata, use configData as fallback
    if (subscription.plan === 'custom') {
      const customCompanyResolutions = 
        subscription.metadata?.custom_resolutions || 
        subscription.custom_resolutions || 
        configData.company_resolutions_limit || 0;
      
      const customPersonResolutions = 
        subscription.metadata?.custom_person_resolutions || 
        configData.person_resolutions_limit || 0;

      console.log(`[${new Date().toISOString()}] [getSubscriptionLimits] Using fallback custom limits:`, {
        customCompanyResolutions,
        customPersonResolutions
      });

      return { 
        companiesLimit: Number(customCompanyResolutions),
        emailsLimit: Number(customCompanyResolutions) * 2,
        personResolutionsLimit: Number(customPersonResolutions)
      };
    }

    // For standard plans, return the values from the plan configuration
    return {
      companiesLimit: configData.company_resolutions_limit || 0,
      emailsLimit: (configData.company_resolutions_limit || 0) * 2,
      personResolutionsLimit: configData.person_resolutions_limit || 0
    };
  } catch (error) {
    console.error('[getSubscriptionLimits] Error in getSubscriptionLimits:', error);
    return { companiesLimit: 0, emailsLimit: 0, personResolutionsLimit: 0 };
  }
}
